import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExifOrientation, TenantConfig } from '../../../../../app.constants';
import { DynamicProperty, DYNAMIC_PROPERTY } from '../../../../../_objects/dynamic-property';
import { InspectionLocation } from '../../../../../_objects/inspection';
import { MarkItem } from '../../../../../_objects/mark';
import { OfflineReportPreviewService, DataProperty } from '../../../../../_services/offline-report-preview.service';
import { LoginUserOfflineService } from '../../../../../_services/offline/login-user-offline.service';
import { MarkOfflineService } from '../../../../../_services/offline/mark-offline.service';
import { ReportPreviewContentService } from '../../../../../_services/report-preview-content.service';
import { TableColumn } from '../../../../models/report-preview/report-template';
import { ImageComponent, TableData } from '../../../../models/report-preview/TableData';
import { ReportMeasurementResult } from '../../../../services/legacy-report-measurement.service';

@Component({
    selector: 'app-report-column',
    templateUrl: './report-column.component.html',
    styleUrls: ['./report-column.component.scss'],
    standalone: false
})
export class ReportColumnComponent implements OnInit, OnChanges {

  @Input() column: TableColumn;
  @Input() columns: TableColumn[];
  @Input() data: TableData;
  @Input() trHeight: number;
  @Input() sizeUnit: string;
  @Input() tableWidth: number;
  @Input() tableHeight: number;
  @Input() tableHeaderHeight: number;
  @Input() pageBreakRow: number;
  @Input() pageKey: string;
  @Input() pictureKey: string;
  @Input() dataMode: number;

  readonly keys = DYNAMIC_PROPERTY.KEY;
  readonly MirrorHorizontalAndRotate270 = 5;
  properties: DynamicProperty[] = [];
  marks: MarkItem[];

  srcList: string[];

  imageHeight: number;

  widthRatio: number;
  columnWidth: number;

  fileMark = TenantConfig.DefaultMarkWhenUploadingNoMarkImage;

  readonly divStyle = 'overflow: hidden; word-break: normal; white-space: pre-wrap;';
  readonly horizontalStyle = 'overflow: hidden; word-break: normal; padding: 0 10px; display: inline-block; white-space: pre-wrap;';
  readonly divInlineStyle = 'display: inline; overflow: hidden; word-break: normal; white-space: pre-wrap;';
  readonly divBlockStyle = ' display: block; word-break: normal; white-space: pre-wrap;';
  readonly relativeStyle = ' height: 100%; position: relative;';
  readonly heatMaxStyle = ' height: 100%;';
  readonly flexStyle = ' display: flex;	display: -webkit-box;	display: -webkit-flex; display: -ms-flexbox; align-items: center;'
    + ' -webkit-box-align: center; -webkit-align-items: center; -ms-flex-align: center; justify-content: center;'
    + ' -webkit-box-pack: center; -webkit-justify-content: center;-ms-flex-pack: center;';
  readonly anchorLinkStyle = 'color: -webkit-link; cursor: pointer; text-decoration: underline;';

  readonly classMarkStyle = 'position: absolute; top: 50%; left: 50%; transform: translateY(-50%) translateX(-50%); -webkit-transform: translateY(-50%) translateX(-50%);';
  readonly space = '　  ';
  readonly divSpace = ' ';
  readonly divSpaceStyle = 'overflow: hidden; word-break: normal; white-space: pre-wrap; display: inline-block;';

  constructor(
    private markService: MarkOfflineService,
    private reportPreviewContentService: ReportPreviewContentService,
    private offlineReportPreviewService: OfflineReportPreviewService,
    private loginUserService: LoginUserOfflineService,
    private translate: TranslateService,
  ) { }

  get isSaveHtml(): boolean {
    return this.offlineReportPreviewService?.isSaveHtml;
  }

  ngOnInit(): void {
    this.markService.getMark()
      .subscribe(result => this.marks = result.items);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.column) {
      if (this.column.textsInfo && this.column.textsInfo?.length != 0) {
        this.properties = this.column.textsInfo.map(text => {
          if (text.type === 'static') {
            return new DynamicProperty(text.text);
          } else if (text.type === 'dynamic') {
            return new DynamicProperty(text.dynamicId);
          } else if (text.type === 'dynamic-editable') {
            const currentUser = this.reportPreviewContentService.userSchemas.find(u => u.id === this.loginUserService.loginUser.id);
            const pageNum = this.offlineReportPreviewService.getPageNum(this.pageKey);
            const pageTotalNum = this.offlineReportPreviewService.getPageTotalNum();
            const imageId = (this.data.images?.find(_ => !_.isDummy)?.id ?? '');
            const addionalEditableId = '_' + this.data.id + (this.dataMode === DataProperty.All ? '_' + imageId : '');

            const rtnText = this.reportPreviewContentService.getEditableContent(text, text.format,
              this.reportPreviewContentService.reportSchema, this.reportPreviewContentService.inspectionSchema,
              currentUser, this.reportPreviewContentService.userSchemas, this.reportPreviewContentService.tenantSchema.name,
              pageNum, pageTotalNum, null, addionalEditableId);

            return new DynamicProperty(rtnText);
          }
        });
      } else {
        this.properties = this.column.texts.map(text => new DynamicProperty(text));
      }
    }

    if (changes.trHeight) {
      this.imageHeight = this.trHeight - 10;
    }

    if (changes.columns) {
      this.widthRatio = this.column.widthRatio;
      if (this.widthRatio === 0) {
        let widthRatioSum = 0;
        this.columns.forEach(c => {
          widthRatioSum += c.widthRatio;
        });
        this.widthRatio = 100 - widthRatioSum;
      }
    }
  }

  isTargetMark(mark: number) {
    return this.marks?.some(m => m.identifier === mark);
  }

  getMarkItem(mark: number) {
    return this.marks?.find(m => m.identifier === mark);
  }

  getOrientationStyle(image: ImageComponent) {
    if (5 <= image.orientation && image.orientation <= 8) {
      return `height: ${image.maxWidth}${this.sizeUnit}; width: ${image.maxHeight}${this.sizeUnit}`;
    }
    return '';
  }

  getInspLocationComment() {
    const inspSchema = this.reportPreviewContentService.inspectionSchema;
    const inspLocations = JSON.parse(inspSchema.inspectionLocations) as InspectionLocation[];

    return inspLocations.find(l => l.identifier === this.data.id)?.comment;
  }

  clickImage(imageId: string) {
    top.postMessage({
      action: 'EditImage',
      message: imageId
    }, '*');
  }

  getImageWidth(width: number) {
    return width && width !== 0
      ? width
      : this.getImageMaxWidth();
  }

  getImageMaxWidth() {
    return this.tableWidth * (this.widthRatio / 100);
  }

  getImageHeight(height: number) {
    return height && height !== 0
      ? height
      : (this.tableHeight - this.tableHeaderHeight) / this.pageBreakRow;
  }

  getImageMaxHeight() {
    if (this.column.textsInfo && this.column.textsInfo?.length != 0) {
      return this.column.textsInfo.length > 1
        ? this.trHeight - 10
        : this.trHeight;
    } else {
      return this.column.texts.length > 1
        ? this.trHeight - 10
        : this.trHeight;
    }
  }
  public scrollToHashLink(id: string) {
    const el = document.getElementById(id);
    el.scrollIntoView();
  }

  hasDummyImage(images: ImageComponent[]) {
    return images.some(image => {
      if (image.isDummy) {
        return true;
      }
    });
  }

  isDoubleHeight(image: ImageComponent) {
    if (image.usedImageMode === 0) {
      if (image.orientation >= ExifOrientation.MirrorHorizontalAndRotate270) {
        return image.height < image.width;
      } else {
        return image.height > image.width;
      }
    } else if (image.usedImageMode === 1) {
      if (image.orientation >= ExifOrientation.MirrorHorizontalAndRotate270) {
        return image.modifiedHeight < image.modifiedWidth;
      } else {
        return image.modifiedHeight > image.modifiedWidth;
      }
    } else {
      return false;
    }
  }
}
